import type { PayPeriodRow, PayrollTotals } from '../types';
import { canonicalMoney, multiplyMoney } from '../lib/money';

const fmt = canonicalMoney;
function fmtDate(d: Date): string { return d.toISOString().split('T')[0]; }

// 2026 Canadian estimate rates
const CPP_RATE = 0.0595;
const EI_RATE = 0.0163;

function estimateDeductions(gross: number) {
  const cpp = Math.round(gross * CPP_RATE * 100) / 100;
  const ei = Math.round(gross * EI_RATE * 100) / 100;
  // Simplified federal tax brackets (2026 est)
  const annualized = gross * 26; // biweekly periods
  let fedRate = 0.15;
  if (annualized > 55867) fedRate = 0.205;
  if (annualized > 111733) fedRate = 0.26;
  const fedTax = Math.round(gross * fedRate * 100) / 100;
  // Ontario provincial (simplified)
  let provRate = 0.0505;
  if (annualized > 51446) provRate = 0.0915;
  if (annualized > 102894) provRate = 0.1116;
  const provTax = Math.round(gross * provRate * 100) / 100;
  const net = Math.round((gross - cpp - ei - fedTax - provTax) * 100) / 100;
  return { cpp, ei, fedTax, provTax, net };
}

export function exportPayrollCSV(
  data: PayPeriodRow[],
  totals: PayrollTotals,
  weekStart: Date,
  weekEnd: Date,
) {
  const startStr = fmtDate(weekStart);
  const endStr = fmtDate(weekEnd);

  const lines: string[] = [
    '"PAYROLL SUMMARY — ESTIMATES ONLY — Verify with your accountant before filing"',
    '',
    `"Pay Period: ${startStr} to ${endStr}"`,
    '',
    'Employee Name,Employment Type,Pay Period Start,Pay Period End,Regular Hours,Overtime Hours,Total Hours,Hourly Rate,Regular Pay,Overtime Pay (1.5x),Gross Pay,CPP (est),EI (est),Federal Tax (est),Provincial Tax (est),Net Pay (est)',
  ];

  for (const r of data) {
    // Rule #34: hourlyRate is money; overtime rate = 1.5× hourly; regularHours/overtimeHours are number quantities.
    const regularPay = Number(multiplyMoney(canonicalMoney(r.hourlyRate), r.regularHours));
    const otPay = Number(multiplyMoney(canonicalMoney(r.hourlyRate), r.overtimeHours * 1.5));
    const d = estimateDeductions(r.grossPay);
    lines.push(
      `"${r.name}",${r.employmentType},${startStr},${endStr},${fmt(r.regularHours)},${fmt(r.overtimeHours)},${fmt(r.actualHours)},${fmt(r.hourlyRate)},${fmt(regularPay)},${fmt(otPay)},${fmt(r.grossPay)},${fmt(d.cpp)},${fmt(d.ei)},${fmt(d.fedTax)},${fmt(d.provTax)},${fmt(d.net)}`
    );
  }

  // Totals row
  const totalRegPay = Math.round(totals.regularHours * (data[0]?.hourlyRate ?? 0) * 100) / 100;
  const totalOtPay = Math.round(totals.overtimeHours * (data[0]?.hourlyRate ?? 0) * 1.5 * 100) / 100;
  void totalRegPay; void totalOtPay;
  lines.push('');
  lines.push(`"TOTAL",,,,"${fmt(totals.regularHours)}","${fmt(totals.overtimeHours)}","${fmt(totals.actualHours)}",,,,${fmt(totals.grossPay)},,,,,`);
  lines.push('');
  lines.push(`"Generated by CrewBit on ${fmtDate(new Date())}. These are estimates. Consult your accountant for final payroll calculations."`);

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `payroll-summary-${startStr}-to-${endStr}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
